/**
 * Deterministic RNG. Every random decision the server makes flows through one of these,
 * seeded from (matchSeed, round, phase), so a resolution can be replayed exactly — which
 * is what lets the client animate a strike it did not itself compute.
 */
export class Rng {
    s;
    constructor(seed) {
        this.s = typeof seed === 'string' ? hashString(seed) : seed >>> 0;
        if (this.s === 0)
            this.s = 0x9e3779b9;
    }
    /** mulberry32 — small, fast, good enough spectral properties for gameplay dice. */
    next() {
        this.s = (this.s + 0x6d2b79f5) >>> 0;
        let t = this.s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }
    /** Integer in [0, n). */
    int(n) {
        return Math.floor(this.next() * n);
    }
    /** Integer in [lo, hi] inclusive. */
    range(lo, hi) {
        return lo + this.int(hi - lo + 1);
    }
    float(lo, hi) {
        return lo + this.next() * (hi - lo);
    }
    chance(p) {
        return this.next() < p;
    }
    pick(xs) {
        return xs[this.int(xs.length)];
    }
    /** Fisher-Yates, in place. */
    shuffle(xs) {
        for (let i = xs.length - 1; i > 0; i--) {
            const j = this.int(i + 1);
            [xs[i], xs[j]] = [xs[j], xs[i]];
        }
        return xs;
    }
    /** Standard normal via Box-Muller. Used for CEP scatter. */
    gaussian() {
        let u = 0;
        while (u === 0)
            u = this.next();
        const v = this.next();
        return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    }
    /** A point drawn from a 2D gaussian of the given sigma — the impact scatter model. */
    scatter(sigma) {
        return { dx: this.gaussian() * sigma, dy: this.gaussian() * sigma };
    }
}
export function hashString(s) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
/** Sub-seed derivation, so parallel systems in one phase never share a stream. */
export function subSeed(seed, ...parts) {
    return `${seed}:${parts.join(':')}`;
}
//# sourceMappingURL=rng.js.map